/** Backend URL (Render). Used when no env or when backend is on Render. */
const RENDER_API_URL = 'https://homeandown-backend-p6wa.onrender.com';

/** True when the app is served from production domain (homeandown.com). */
function isProductionOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname.toLowerCase();
  return h === 'homeandown.com' || h.endsWith('.homeandown.com');
}

export function getPyApiBase(): string {
  const envUrl = (import.meta as any).env?.VITE_PY_API_URL as string | undefined;
  const trimmed = envUrl != null ? String(envUrl).trim() : '';
  // Explicit same-origin: set VITE_PY_API_URL=same when backend is on same domain (e.g. WinSCP at /api)
  if (trimmed.toLowerCase() === 'same' && typeof window !== 'undefined' && isProductionOrigin()) return '';
  if (!trimmed) return RENDER_API_URL;
  return trimmed.replace(/\/$/, '');
}

// Test backend connection on module load (only in browser)
if (typeof window !== 'undefined') {
  const testConnection = async () => {
    const baseUrl = getPyApiBase();
    try {
      const response = await fetch(`${baseUrl}/api`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        console.warn('[Backend Connection] ⚠️ Backend responded with status:', response.status);
      }
    } catch (error: any) {
      console.error('[Backend Connection] ❌ Failed to connect to backend:', baseUrl);
      console.error('[Backend Connection] Error:', error.message);
    }
  };
  
  // Test connection after a short delay to avoid blocking initial render
  setTimeout(testConnection, 1000);
}

// Helper function to get the full API URL for a given path
export function getApiUrl(path: string): string {
  const base = getPyApiBase();
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

export async function pyFetch(path: string, options: RequestInit & { useApiKey?: boolean } = {}) {
  const base = getPyApiBase();
  
  // Build the full URL - all paths should use the configured API base
  const url = `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  
  // Initialize headers - start with empty object to ensure we control the order
  const headers: Record<string, string> = {};
  
  // First, try to get auth token from storage and add it to headers
  // This ensures authentication works even if cookies are blocked
  // Always check for token - don't require auth_profile to exist
  try {
    // First try sessionStorage (preferred)
    let storedToken = sessionStorage.getItem('auth_token');
    
    // Fallback to localStorage if sessionStorage doesn't have it
    if (!storedToken) {
      storedToken = localStorage.getItem('auth_token');
    }
    
    // If still no token, try to get from cookie as last resort
    // This handles cases where storage might be blocked but cookies work
    if (!storedToken) {
      try {
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === 'auth_token' && value) {
            storedToken = value;
            // Also store it in sessionStorage for future use
            try {
              sessionStorage.setItem('auth_token', value);
              localStorage.setItem('auth_token', value);
            } catch (e) {
              // Ignore storage errors
            }
            break;
          }
        }
      } catch (e) {
        // Ignore cookie errors
      }
    }
    
    if (storedToken && storedToken.trim()) {
      headers['Authorization'] = `Bearer ${storedToken.trim()}`;
      } else {
        // No token in storage - this is OK for endpoints that accept cookies
        // httponly cookies are sent automatically by the browser
        // We'll rely on cookie-based authentication for these requests
        // Don't log this - it's expected behavior, not an error
      }
  } catch (e) {
    // Ignore storage errors
    console.warn('[pyFetch] Error reading auth token from storage:', e);
  }
  
  // Now merge any headers passed in options (but don't override Authorization if we set it)
  if (options.headers) {
    Object.assign(headers, options.headers);
    // If options.headers has Authorization, it will override ours (which is fine if explicitly set)
  }
  
  // Only set Content-Type for JSON requests (if not already set)
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  const apiKey = (import.meta as any).env?.VITE_PYTHON_API_KEY;
  // Only include the API key when explicitly requested. Default to false so
  // browser session (cookie) based auth works by default.
  const wantKey = options.useApiKey ?? false;

  if (wantKey && apiKey && apiKey.trim()) {
    headers['X-API-Key'] = apiKey;
  }
  
  try {
    // Ensure cookies (refresh token cookie and auth_token cookie) are sent/accepted when calling
    // the Python API so session-based endpoints like /auth/me work.
    // Add timeout to prevent hanging requests
    // Use shorter timeout for auth endpoints (5s) vs other endpoints (10s for properties, 5s for notifications)
    const isAuthEndpoint = path.includes('/auth/'); // signup, login, logout, me, etc.
    const isPropertiesEndpoint = path.includes('/properties');
    const isNotificationsEndpoint = path.includes('/notifications');
    const isAdminEndpoint = path.includes('/admin/');
    
    let timeoutMs = 30000; // Default 30s
    if (isAuthEndpoint) {
      timeoutMs = 60000; // 60s for auth/signup so cold start and slow mobile networks can complete
    } else if (isAdminEndpoint) {
      timeoutMs = 5000; // 5s for admin endpoints (optimized backend should respond faster)
    } else if (isPropertiesEndpoint) {
      timeoutMs = 30000; // 30s for properties (increased to handle slow database queries)
    } else if (isNotificationsEndpoint) {
      timeoutMs = 5000; // 5s for notifications
    }
    
    const startTime = performance.now();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const resp = await fetch(url, { 
        ...options, 
        headers, 
        credentials: 'include',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!resp.ok) {
        const text = await resp.text();
        let errorMessage = text || `API request failed with status ${resp.status}`;
        
        // Try to parse JSON error response
        try {
          const errorJson = JSON.parse(text);
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch {
          // Not JSON, use text as-is
        }
        
        const error = new Error(errorMessage);
        (error as any).status = resp.status;
        // Add flag to indicate if it's an auth error (401)
        (error as any).isAuthError = resp.status === 401;
        
        // If it's a 401, don't immediately clear token - let AuthContext handle it
        // The token might still be valid for other endpoints, or user might be logged in via cookies
        // Only clear if it's explicitly an auth endpoint failure
        if (resp.status === 401 && (path.includes('/auth/') || path.includes('/login'))) {
          // Only clear token for auth endpoint failures
          try {
            sessionStorage.removeItem('auth_token');
            localStorage.removeItem('auth_token');
          } catch (e) {
            // Ignore storage errors
          }
        }
        
        throw error;
      }
      
      const ct = resp.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const data = await resp.json();
        return data;
      }
      
      const text = await resp.text();
      return text;
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError' || fetchError.name === 'AbortSignal') {
        throw new Error('Request timeout - the server took too long to respond');
      }
      // Handle connection errors gracefully
      if (fetchError.message?.includes('ERR_CONNECTION_REFUSED') || 
          fetchError.message?.includes('Failed to fetch') ||
          fetchError.name === 'TypeError' ||
          (fetchError.message && typeof fetchError.message === 'string' && 
           (fetchError.message.includes('network') || fetchError.message.includes('fetch')))) {
        throw new Error(`NetworkError: Could not reach ${base} - is the backend running? Check CORS settings if backend is accessible.`);
      }
      throw fetchError;
    }
  } catch (error: any) {
    // Network-level failures (cannot reach backend)
    
    if (error && (error.name === 'TypeError' || error.message === 'Failed to fetch')) {
      console.error(`[pyFetch] Network error - Could not reach backend at ${base}`);
      console.error(`[pyFetch] Full error:`, {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      throw new Error(`NetworkError: Could not reach ${base} - is the backend running? Check CORS settings if backend is accessible.`);
    }

    // If the thrown error contains a JSON payload string (from the server), try to parse
    const raw = error && error.message ? error.message : String(error);
    try {
      const parsed = JSON.parse(raw);
      // Prefer common shapes: { detail: '...' } or { message: '...' } or { detail: { message: '...' } }
      const candidate = parsed?.detail?.message || parsed?.detail || parsed?.message || (typeof parsed === 'string' ? parsed : undefined);
      throw new Error(candidate || 'API request failed');
    } catch (_parseErr) {
      // Not JSON or failed parsing — rethrow a friendly message
      throw new Error(raw || 'API request failed');
    }
  }
}